import {
  type ComponentPropsWithoutRef,
  memo,
  useCallback,
  useMemo,
} from "react";

import { parseGid } from "@shopify/hydrogen-react";
import { type AttributeInput } from "@shopify/hydrogen-react/storefront-api-types";

import { type ButtonType, Button } from "@components/Button";
import {
  isColorOption,
  isProductFixedBundle,
  isStandaloneBundleKit,
  isVariantFixedBundle,
  isMigratedBundle,
} from "@react/utilities";
import {
  type CartProductVariant,
  type CartProduct,
  ThemeMode,
} from "@root/tablescape/state";
import {
  addIntelligemsLineItemAttribute,
  getMsrpForVariant,
} from "@root/tablescape/utilities";
import {
  TablescapeContext,
  TablescapeEvent,
} from "@root/tablescape/utilities/tablescapeContext";
import { STOREFRONT_CART_ATTRIBUTES } from "@tablescape/constants";
import { tw, type TTailwindString } from "classnames";

type Props = {
  attributes?: AttributeInput[];
  buttonTextOverride?: string;
  buttonTypeOverride?: ButtonType;
  isIntelligemsEnabled: boolean;
  products?: CartProduct[];
  theme: ThemeMode;
  variants?: CartProductVariant[];
} & Pick<
  ComponentPropsWithoutRef<typeof Button>,
  "mtrAs" | "mtrTarget" | "mtrTrack" | "rounded"
>;

const styles = {
  base: (isWaitlisted: boolean) =>
    tw({ ["klaviyo-bis-trigger" as TTailwindString]: isWaitlisted }),
};

const AddToCartButtonBase = ({
  attributes,
  buttonTextOverride,
  buttonTypeOverride,
  isIntelligemsEnabled,
  mtrAs = "clickAddFromModule",
  mtrTarget = "cta",
  mtrTrack = "click",
  products,
  rounded,
  theme,
  variants,
}: Props) => {
  const isAvailable = useMemo(
    () => variants?.every((variant) => variant.availableForSale),
    [variants]
  );

  const isWaitlisted = useMemo(
    () =>
      !isAvailable &&
      !variants?.some((variant) => variant.disableWaitlist) &&
      !(
        (products && isProductFixedBundle(products[0])) ||
        (variants && isVariantFixedBundle(variants)) ||
        (products && isMigratedBundle(products[0]))
      ) &&
      variants?.length === 1,
    [isAvailable, variants, products]
  );

  const buttonType = useMemo(
    () =>
      buttonTypeOverride ||
      ((isWaitlisted && theme === ThemeMode.DARK
        ? "secondary-alt"
        : isAvailable
        ? "primary-alt"
        : "primary") as ButtonType),
    [buttonTypeOverride, isAvailable, isWaitlisted, theme]
  );

  const buttonText = useMemo(
    () =>
      buttonTextOverride ??
      (isAvailable
        ? "Add to cart"
        : isWaitlisted
        ? "Sign up for waitlist"
        : "Sold out"),
    [isWaitlisted, isAvailable, buttonTextOverride]
  );

  const bundleProduct = useMemo(() => {
    if (products && isProductFixedBundle(products[0])) {
      const productFixedBundle = products[0];
      return {
        compareAtPrice: productFixedBundle.variants[0].compareAtPrice
          ? Number(productFixedBundle.variants[0].compareAtPrice.amount) * 100
          : undefined,
        id: parseGid(productFixedBundle.id).id,
        price: Number(productFixedBundle.variants[0].price.amount) * 100,
        title: productFixedBundle.title,
        imageURL:
          productFixedBundle.variants[0].imageUrl?.replace(/^https?:/, "") ??
          undefined,
      };
    }

    if (products && isMigratedBundle(products[0]) && variants) {
      const migratedBundle = products[0];
      return {
        compareAtPrice: variants[0].compareAtPrice
          ? Number(variants[0].compareAtPrice.amount) * 100
          : undefined,
        id: parseGid(migratedBundle.id).id,
        price: Number(variants[0].price.amount) * 100,
        title: migratedBundle.title,
        imageURL: variants[0].imageUrl?.replace(/^https?:/, "") ?? undefined,
      };
    }

    if (variants && isVariantFixedBundle(variants)) {
      return {
        compareAtPrice: variants[0].compareAtPrice
          ? Number(variants[0].compareAtPrice?.amount) * 100
          : undefined,
        id: parseGid(variants[0].product?.id).id,
        price: Number(variants[0].price.amount) * 100,
        title: variants[0].product?.title,
        imageURL: variants[0].imageUrl?.replace(/^https?:/, "") ?? undefined,
      };
    }
  }, [products, variants]);

  const autoAddVariants = useMemo(() => {
    const autoAddVariants: CartProductVariant[] = [];
    variants?.forEach((variant) => {
      variant.giftWithPurchase?.autoAddProducts?.forEach((product) => {
        const currentVariantColor = variant.selectedOptions.find((option) =>
          isColorOption(option)
        )?.value;
        let matchingVariant;
        if (currentVariantColor) {
          matchingVariant = product.variants.find(
            (variant) =>
              variant.selectedOptions.find((option) => isColorOption(option))
                ?.value === currentVariantColor
          );
        }

        const firstAvailableVariant = product.variants.find(
          (variant) => variant.availableForSale
        );

        if (matchingVariant) {
          autoAddVariants.push(matchingVariant);
        } else if (firstAvailableVariant) {
          autoAddVariants.push(firstAvailableVariant);
        }
      });
    });
    return autoAddVariants;
  }, [variants]);

  const lines = useMemo(() => {
    if (!variants) {
      return;
    }
    return (
      isVariantFixedBundle(variants)
        ? variants[0].variantFixedBundleComponents
        : autoAddVariants
        ? variants.concat(autoAddVariants)
        : variants
    )?.map((variant) => {
      const isKit = isStandaloneBundleKit(variant);

      const lineAttributes = [
        {
          key: STOREFRONT_CART_ATTRIBUTES.MSRP,
          value: String(getMsrpForVariant(variant)),
        },
        ...(variant.shippingNote
          ? [
              {
                key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                value: String(true),
              },
            ]
          : []),
        ...(isKit
          ? [
              {
                key: STOREFRONT_CART_ATTRIBUTES.STANDALONE_BUNDLE_KIT,
                value: String(true),
              },
            ]
          : []),
        ...(attributes || []),
      ];

      return {
        attributes: isIntelligemsEnabled
          ? addIntelligemsLineItemAttribute(
              parseGid(variant.id).id,
              lineAttributes
            ) ?? lineAttributes
          : lineAttributes,
        id: variant.id,
        quantity: 1,
      };
    });
  }, [variants, isIntelligemsEnabled, autoAddVariants, attributes]);

  const handleClick = useCallback(() => {
    if (isAvailable) {
      TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
        ui: {
          cartDrawerOpen: true,
          cartDrawerUserToggled: false,
          giftNoteDrawerOpen: false,
          productVariantDrawerOpen: false,
        },
      });

      TablescapeContext.emit(
        bundleProduct
          ? TablescapeEvent.ADD_BUNDLE_TO_CART
          : TablescapeEvent.ADD_TO_CART,
        {
          cart: {
            ...(bundleProduct ? { bundleProduct } : {}),
            lines,
          },
        }
      );
    }
  }, [isAvailable, bundleProduct, lines]);

  return (
    <Button
      className={styles.base(isWaitlisted)}
      disabled={!variants || (!isAvailable && !isWaitlisted)}
      fluid
      isLoading={!variants}
      mtrAs={mtrAs}
      mtrTarget={mtrTarget}
      mtrTrack={mtrTrack}
      onClick={handleClick}
      rounded={rounded}
      type={buttonType}
    >
      {buttonText}
    </Button>
  );
};

export const AddToCartButton = memo(AddToCartButtonBase);
